/**
 * Phase 2: Frontend Validation Data Service
 * 
 * Handles decryption and validation of NIA person data for claims form validation
 * without exposing sensitive information to the user interface.
 */

import CryptoJS from 'crypto-js';

/**
 * Decrypt validation data received from backend
 * Note: This is a placeholder for the actual decryption logic
 * In production, this would need to be implemented with proper security
 * 
 * @param {Object} validationData - Encrypted validation data from backend
 * @returns {Object|null} Decrypted person data or null if invalid
 */
export const decryptValidationData = (validationData) => {
  if (!validationData || !validationData.encryptedPersonData) {
    console.log('🔒 No validation data available for decryption');
    return null;
  }
  
  try {
    // Validate token expiration
    if (validationData.expiresAt) {
      const expiresAt = new Date(validationData.expiresAt);
      if (new Date() > expiresAt) {
        console.log('🔒 Validation data has expired');
        return null;
      }
    }
    
    // TODO: Implement actual decryption
    // For now, this is a placeholder that logs the encrypted data
    console.log('🔐 Validation data received for decryption:', {
      hasPersonNameHash: !!validationData.personNameHash,
      hasDateOfBirthHash: !!validationData.dateOfBirthHash,
      hasEncryptedData: !!validationData.encryptedPersonData,
      hasValidationToken: !!validationData.validationToken,
      expiresAt: validationData.expiresAt
    });
    
    // Return structure that indicates validation data is available
    // but doesn't expose the actual sensitive data
    return {
      isAvailable: true,
      personNameHash: validationData.personNameHash,
      dateOfBirthHash: validationData.dateOfBirthHash,
      expiresAt: validationData.expiresAt,
      // Note: We don't expose the actual decrypted person data
      // The validation will be done using hashes
    };
    
  } catch (error) {
    console.error('🔒 Failed to decrypt validation data:', error);
    return null;
  }
};

/**
 * Validate user input against NIA person data using hashes
 * This allows validation without exposing the actual NIA data
 * 
 * @param {string} fieldName - Name of the field being validated
 * @param {string} userInput - User's input to validate
 * @param {Object} validationData - Decrypted validation data
 * @returns {Object} Validation result
 */
export const validateFieldAgainstNIA = (fieldName, userInput, validationData) => {
  if (!validationData || !validationData.isAvailable) {
    return {
      isValid: true,
      message: null,
      source: 'no_validation_data'
    };
  }
  
  switch (fieldName) {
    case 'claimantName':
    case 'deceasedName':
      // Normalize name input for comparison (remove special chars, standardize)
      const normalizedNameInput = normalizeForComparison(userInput);
      return validateNameField(normalizedNameInput, validationData);
      
    case 'claimantDateOfBirth':
    case 'deceasedDateOfBirth':
      // For dates, pass raw input directly - don't use normalizeForComparison as it removes dashes
      return validateDateOfBirthField(userInput, validationData);
      
    default:
      return {
        isValid: true,
        message: null,
        source: 'field_not_validated'
      };
  }
};

/**
 * Validate name field against NIA data using hash comparison
 * @param {string} normalizedInput - Normalized user input
 * @param {Object} validationData - Validation data with hashes
 * @returns {Object} Validation result
 */
const validateNameField = (normalizedInput, validationData) => {
  if (!validationData.personNameHash || !normalizedInput) {
    return {
      isValid: true,
      message: null,
      source: 'no_hash_available'
    };
  }
  
  // Create hash of user input for comparison
  const inputHash = createSimpleHash(normalizedInput);
  
  // Debug logging to help with troubleshooting
  console.log('🔍 Name validation debug:', {
    userInput: normalizedInput,
    userInputHash: inputHash,
    expectedHash: validationData.personNameHash,
    hashesMatch: inputHash === validationData.personNameHash
  });
  
  // Compare hashes (this is a simplified implementation)
  const isValid = inputHash === validationData.personNameHash;
  
  return {
    isValid,
    message: isValid ? null : 'Name does not match verification data',
    source: 'nia_validation',
    confidence: isValid ? 'high' : 'mismatch'
  };
};

/**
 * Validate date of birth against NIA data using hash comparison
 * @param {string} rawInput - Raw date input (not pre-normalized)
 * @param {Object} validationData - Validation data with hashes
 * @returns {Object} Validation result
 */
const validateDateOfBirthField = (rawInput, validationData) => {
  console.log('🔍 Date validation debug:', {
    rawInput,
    hasDateOfBirthHash: !!validationData.dateOfBirthHash,
    validationData: validationData
  });
  
  if (!validationData.dateOfBirthHash || !rawInput) {
    console.log('🔍 Date validation: No hash or input available');
    return {
      isValid: true,
      message: null,
      source: 'no_hash_available'
    };
  }
  
  // Normalize date to YYYY-MM-DD format (now gets proper date format)
  const normalizedDate = normalizeDateForComparison(rawInput);
  console.log('🔍 Date validation: Normalized date:', normalizedDate);
  
  if (!normalizedDate) {
    console.log('🔍 Date validation: Invalid date format');
    return {
      isValid: false,
      message: 'Invalid date format',
      source: 'format_validation'
    };
  }
  
  // Create hash of normalized date for comparison
  const inputHash = createSimpleHash(normalizedDate);
  
  console.log('🔍 Date validation debug:', {
    userInput: normalizedDate,
    userInputHash: inputHash,
    expectedHash: validationData.dateOfBirthHash,
    hashesMatch: inputHash === validationData.dateOfBirthHash
  });
  
  // Compare hashes
  const isValid = inputHash === validationData.dateOfBirthHash;
  
  const result = {
    isValid,
    message: isValid ? null : 'Date of birth does not match verification data',
    source: 'nia_validation',
    confidence: isValid ? 'high' : 'mismatch'
  };
  
  console.log('🔍 Date validation result:', result);
  
  return result;
};

/**
 * Normalize text for comparison (remove extra spaces, standardize case)
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
const normalizeForComparison = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s]/g, ''); // Remove special characters
};

/**
 * Normalize date for comparison (convert to YYYY-MM-DD format)
 * @param {string|Date} dateInput - Date to normalize
 * @returns {string|null} Normalized date string or null if invalid
 */
const normalizeDateForComparison = (dateInput) => {
  if (!dateInput) return null;
  
  let date;
  if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    date = new Date(dateInput);
  }
  
  if (isNaN(date.getTime())) return null;
  
  // Return in YYYY-MM-DD format
  return date.toISOString().split('T')[0];
};

/**
 * Create a SHA-256 hash for comparison (matches backend implementation)
 * 
 * @param {string} data - Data to hash
 * @returns {string} SHA-256 hash string
 */
const createSimpleHash = (data) => {
  if (!data) return '';
  
  // Use SHA-256 to match backend implementation
  return CryptoJS.SHA256(data.toString()).toString(CryptoJS.enc.Hex);
};

/**
 * Check if validation data is still valid (not expired)
 * @param {Object} validationData - Validation data to check
 * @returns {boolean} True if validation data is still valid
 */
export const isValidationDataValid = (validationData) => {
  if (!validationData || !validationData.isAvailable) {
    return false;
  }
  
  if (validationData.expiresAt) {
    const expiresAt = new Date(validationData.expiresAt);
    return new Date() <= expiresAt;
  }
  
  return true;
};

/**
 * Get validation status for display
 * @param {Object} validationData - Current validation data
 * @returns {Object} Status information for UI
 */
export const getValidationStatus = (validationData) => {
  if (!validationData) {
    return {
      status: 'unavailable',
      message: 'Validation data not available',
      canValidate: false
    };
  }
  
  if (!validationData.isAvailable) {
    return {
      status: 'unavailable',
      message: 'No validation data from verification',
      canValidate: false
    };
  }
  
  if (!isValidationDataValid(validationData)) {
    return {
      status: 'expired',
      message: 'Validation data has expired. Please verify your identity again.',
      canValidate: false
    };
  }
  
  return {
    status: 'available',
    message: 'Form validation against verification data is active',
    canValidate: true,
    expiresAt: validationData.expiresAt
  };
};

export default {
  decryptValidationData,
  validateFieldAgainstNIA,
  isValidationDataValid,
  getValidationStatus
};
